'use client';

import React from 'react';
import { cn } from '@sharpr/ui';
import { RichPopover } from '@sharpr/ui/rich-popover';
import { CloudRain, Wind, Snowflake, Sun } from 'lucide-react';
import { pickSeverity } from '@/services/weather/classify';
import type { GameWeather } from '@/services/weather/types';
import { WeatherDetailCard } from './WeatherDetailCard';
import { WeatherDrawerHeader, formatWeatherLabel } from './WeatherTrigger';

interface WeatherStripProps {
  weather: GameWeather;
  className?: string;
}

function WindLabel({ weather }: { weather: GameWeather }) {
  const mph = Math.round(weather.wind.speedMph);
  const rel = weather.wind.relativeToCf;
  if (rel === 'calm') {
    return <span className="text-gray-400">{mph} mph calm</span>;
  }
  const tone =
    weather.flags.windProfile === 'out' ? 'text-amber-400' :
    weather.flags.windProfile === 'in' ? 'text-blue-400' :
    weather.flags.windProfile === 'crosswind' ? 'text-purple-300' :
    'text-gray-300';
  const verb =
    rel === 'out' ? 'out' :
    rel === 'in' ? 'in' :
    rel === 'cross-lr' ? 'L→R' :
    'R→L';
  return <span className={tone}>{mph} {verb}</span>;
}

function PrecipLabel({ weather }: { weather: GameWeather }) {
  const pct = weather.precip.probabilityPct;
  if (pct < 20) return null;
  const tone =
    weather.flags.rainoutRisk === 'high' ? 'bg-red-500/15 text-red-300' :
    weather.flags.rainoutRisk === 'medium' ? 'bg-amber-500/15 text-amber-300' :
    'bg-blue-500/15 text-blue-300';
  return (
    <span className={cn('inline-flex items-center gap-0.5 px-1 py-px rounded font-medium text-[9px]', tone)}>
      <CloudRain className="w-2.5 h-2.5" />
      {pct}%
    </span>
  );
}

function TempLabel({ weather }: { weather: GameWeather }) {
  const t = Math.round(weather.tempF);
  if (weather.flags.extremeCold) return <span className="text-cyan-300">{t}°</span>;
  if (weather.flags.extremeHeat) return <span className="text-orange-300">{t}°</span>;
  return <span>{t}°</span>;
}

function StripContent({ weather, className }: WeatherStripProps) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded font-medium text-[9px] bg-gray-500/10 text-gray-300 hover:bg-gray-500/20 hover:text-gray-100 cursor-pointer transition-colors', className)}>
      <TempLabel weather={weather} />
      <span className="text-gray-400">·</span>
      <Wind className="w-2.5 h-2.5 text-gray-400" />
      <WindLabel weather={weather} />
      {weather.precip.probabilityPct >= 20 && <span className="text-gray-400">·</span>}
      <PrecipLabel weather={weather} />
      {weather.flags.extremeCold && !weather.precip.probabilityPct && <Snowflake className="w-2.5 h-2.5 text-cyan-300" />}
      {weather.flags.extremeHeat && <Sun className="w-2.5 h-2.5 text-orange-300" />}
    </span>
  );
}

export function WeatherStrip({ weather, className }: WeatherStripProps) {
  if (weather.indoorVenue) return null;
  const sev = pickSeverity(weather);
  return (
    <RichPopover
      trigger={<StripContent weather={weather} className={className} />}
      header={<WeatherDrawerHeader weather={weather} />}
      ariaLabel={`Weather: ${formatWeatherLabel(sev)}`}
      width={320}
      showArrow={false}
      drawerDescription="Weather forecast and wind details for first pitch."
    >
      <WeatherDetailCard weather={weather} className="rounded-none border-0 shadow-none w-full" />
    </RichPopover>
  );
}
